import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/deploy/components/ui/Button';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { ApiError } from '@/deploy/services/api';
import {
  containerReleasesService,
  type ContainerRelease,
} from '@/deploy/services/container-releases.service';
import { useSessionStore } from '@/deploy/stores/session.store';
import { can } from '@/deploy/utils/permissions';
import { formatRelative } from '@/deploy/utils/format';
import styles from './RetryRelease.module.scss';

// ============================================================
// Bouton « Réessayer » d'une release de container en échec
// ============================================================
// Relance le workflow GitHub en échec (update.yml ou release.yml) côté CICD et reprend le suivi,
// au lieu de tout annuler et de relancer une nouvelle version. Le CICD refuse (409) si la release
// n'est plus en échec, si une relance tourne déjà ou si une autre release du container l'interdit.
// ============================================================

const WORKFLOW_OF: Partial<Record<ContainerRelease['status'], string>> = {
  'update-failed': 'update.yml',
  failed: 'release.yml',
};

/** Message lisible pour une relance refusée. */
export function retryErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return 'Il te faut le droit « Déployer vers Bêta » (cicd.deploy.beta) pour relancer une release.';
    if (e.status === 404) return 'Release introuvable : elle a peut-être été supprimée, recharge la page.';
    return e.message || `Relance refusée (HTTP ${e.status}).`;
  }
  // fetch rejette en TypeError quand le CICD ne répond pas (réseau, CORS)
  if (e instanceof TypeError) return 'CICD injoignable : vérifie ta connexion puis réessaie.';
  return e instanceof Error ? e.message : 'Erreur inconnue';
}

export interface RetryState {
  retry: () => Promise<void>;
  busy: boolean;
  error: string | null;
  clearError: () => void;
  allowed: boolean;
  workflow: string;
}

/**
 * État d'une relance : à brancher sur RetryReleaseButton et RetryError.
 * `retryable` : le bouton est proposé ; une erreur ne lui survit pas (sinon un refus devenu faux
 * réapparaîtrait quand le bouton revient). `onRetried` est attendu avant la fin du chargement,
 * pour que le bouton ne redevienne pas cliquable avant que la vue ait pris le nouvel état ; c'est
 * un rechargement best-effort : la relance est acceptée, son échec n'est jamais affiché comme un
 * refus. `onConflict` : appelé sur un refus 409 (la vue n'était plus à jour), pour la recharger.
 */
export function useRetryRelease(
  projectId: string,
  release: ContainerRelease,
  retryable: boolean,
  onRetried: (updated: ContainerRelease) => void | Promise<void>,
  onConflict?: () => void | Promise<unknown>,
): RetryState {
  const user = useSessionStore((s) => s.user);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    if (!retryable) setError(null);
  }, [retryable]);

  const retry = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      // Seul le POST décide du refus : ses erreurs sont affichées
      let updated: ContainerRelease;
      try {
        updated = await containerReleasesService.retry(projectId, release.containerCode, release.id);
      } catch (e) {
        setError(retryErrorMessage(e));
        if (e instanceof ApiError && e.status === 409 && onConflict) {
          try { await onConflict(); } catch { /* best-effort : l'erreur affichée suffit */ }
        }
        return;
      }
      // Relance acceptée : le rechargement de la vue est best-effort, jamais un refus
      try { await onRetried(updated); } catch { /* le polling ou le prochain rechargement rattrapera */ }
    } finally {
      setBusy(false);
    }
  }, [projectId, release.containerCode, release.id, onRetried, onConflict]);

  return {
    retry,
    busy,
    error,
    clearError,
    allowed: can(user, 'cicd.deploy.beta'),
    workflow: WORKFLOW_OF[release.status] ?? 'le workflow',
  };
}

export function RetryReleaseButton({ state }: { state: RetryState }) {
  return (
    <Button
      variant="softWarning"
      size="sm"
      leftIcon={<Icon name="refresh" size={11} />}
      onClick={() => void state.retry()}
      loading={state.busy}
      disabled={!state.allowed}
      title={state.allowed
        ? `Relancer ${state.workflow} sur GitHub et reprendre le suivi, sans créer de nouvelle version`
        : 'Droit cicd.deploy.beta requis pour relancer une release'}
    >
      {state.busy ? 'Relance…' : 'Réessayer'}
    </Button>
  );
}

export function RetryError({ state }: { state: RetryState }) {
  if (!state.error) return null;
  return (
    <div className={styles.error} role="alert">
      <Icon name="warn" size={12} />
      <span>{state.error}</span>
    </div>
  );
}

/** « réessayé 2 fois par Tintin il y a 3 min » */
function manualRetryLabel(release: ContainerRelease): string {
  const parts = ['réessayé'];
  if ((release.retryCount ?? 0) > 1) parts.push(`${release.retryCount} fois`);
  if (release.lastRetryByName) parts.push(`par ${release.lastRetryByName}`);
  if (release.lastRetryAt) parts.push(formatRelative(release.lastRetryAt));
  return parts.join(' ');
}

/** Mention de la relance automatique (une seule par release) et des relances manuelles. */
export function RetryNotes({ release }: { release: ContainerRelease }) {
  if (!release.autoRetriedAt && !release.retryCount) return null;
  return (
    <span className={styles.notes}>
      {release.autoRetriedAt && (
        <span title={`${release.autoRetryWorkflow ?? 'Le workflow'} a été annulé faute de runner et relancé tout seul ${formatRelative(release.autoRetriedAt)}`}>
          <Badge tone="info">relancé automatiquement une fois</Badge>
        </span>
      )}
      {!!release.retryCount && (
        <span className={styles.manual} title={release.lastRetryAt ? new Date(release.lastRetryAt).toLocaleString('fr-FR') : undefined}>
          {manualRetryLabel(release)}
        </span>
      )}
    </span>
  );
}
